import { useState } from 'react';
import Button from '../../../external/components/Button/Button';
import {
  StyledDd,
  StyledDefWrapper,
  StyledDl,
  StyledDt,
} from '../../../external/components/DefLists';
import { TextInput } from '../../../external/components/Input/Input';
import { Tag } from '../../../external/components/Tag/Tag';

import Text from '../../../external/components/Text/Text';
import { ProviderRpcError } from '../../../types/metamask.types';
import { NetworkId } from '../../../types/network.types';
import { BigNumberHelper } from '../helpers/BigNumbeHelper';
import { useLPStakingActions } from '../hooks/useLpStakingActions';
import { UseLpStakingPool } from '../hooks/usePool';
import { LpScreenEnum } from './_Screens';

interface Props {
  walletNetworkId: NetworkId;
  unstakeAmount: string;
  setUnstakeAmount: React.Dispatch<React.SetStateAction<string>>;
  setScreen: React.Dispatch<React.SetStateAction<LpScreenEnum>>;
}

export function UnstakeScreen({ unstakeAmount, setScreen, setUnstakeAmount }: Props) {
  const [unstakeLoading, setUnstakeLoading] = useState(false);

  const pool = UseLpStakingPool();

  const { stakingTokenSymbol, stakedBalance } = pool;
  const { unstake } = useLPStakingActions({
    pool,
  });

  const unstakeAmountGreaterThanStaked = BigNumberHelper.isGreaterThan(
    unstakeAmount,
    stakedBalance || '0',
  );
  const unstakeAmountLessThanOrZero = BigNumberHelper.isLessThanOrEqualTo(unstakeAmount || 0, 0);

  const disabled = unstakeAmountGreaterThanStaked || unstakeAmountLessThanOrZero || unstakeLoading;

  const handleUnstake = async () => {
    setUnstakeLoading(true);
    try {
      await unstake(unstakeAmount);
      setUnstakeAmount(unstakeAmount);
      setUnstakeLoading(false);
      setScreen(LpScreenEnum.UNSTAKE_SUCCESS);
    } catch (_error) {
      setUnstakeLoading(false);
      const error = _error as ProviderRpcError;
      if (error.code === 4001) {
        // do nothing if RPC error
        return;
      } else {
        setScreen(LpScreenEnum.ERROR);
      }
    }
  };

  const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const regex = new RegExp('^[0-9]*[.,]?[0-9]*$');
    if (regex.test(target.value)) {
      setUnstakeAmount(target.value);
    }
  };

  return (
    <>
      <Button
        size='small'
        variant='secondary'
        dark={true}
        onClick={() => setScreen(LpScreenEnum.HOME_SCREEN)}
        disabled={unstakeLoading}
      >
        ⬅
      </Button>
      <StyledDl>
        <StyledDefWrapper>
          <StyledDt>
            <Text as='span' dark={true} weight={600}>
              Current amount staked
            </Text>
          </StyledDt>
          <StyledDd>
            <>
              <Text as='span' dark={true}>
                {stakedBalance} {stakingTokenSymbol}
              </Text>
            </>
          </StyledDd>
        </StyledDefWrapper>
      </StyledDl>
      <Text dark={true} weight={600} style={{ marginBottom: '0.5rem' }}>
        <label htmlFor='unstake-amount'>Enter the amount you wish to unstake</label>
      </Text>
      <TextInput
        darkAlt={true}
        id='unstake-amount'
        type='text'
        disabled={unstakeLoading}
        max={stakedBalance}
        value={unstakeAmount}
        onChange={handleChange}
        pattern={'^[0-9]*[.,]?[0-9]*$'}
        placeholder='Enter unstake amount'
        autoComplete='off'
      ></TextInput>
      {unstakeAmountGreaterThanStaked && (
        <Text dark={true} size='sm' style={{ margin: '0.5rem 0' }}>
          {' '}
          You do not have enough {stakingTokenSymbol}
        </Text>
      )}
      <br />
      <div className='flex justify-end'>
        <Tag dark={true} onClick={() => setUnstakeAmount(stakedBalance || '0')}>
          {' '}
          100%
        </Tag>
        <Tag
          dark={true}
          onClick={() => setUnstakeAmount(BigNumberHelper.divide(stakedBalance || '0', 4 / 3))}
        >
          {' '}
          75%
        </Tag>
        <Tag
          dark={true}
          onClick={() => setUnstakeAmount(BigNumberHelper.divide(stakedBalance || '0', 2))}
        >
          {' '}
          50%
        </Tag>
        <Tag
          dark={true}
          onClick={() => setUnstakeAmount(BigNumberHelper.divide(stakedBalance || '0', 4))}
        >
          {' '}
          25%
        </Tag>
      </div>
      <Text color='lightgrey' size='sm'>
        * Unstaking is instant
      </Text>
      <Text color='lightgrey' size='sm'>
        * Staking rewards will stop being earned as soon as you click "Confirm Unstake".
      </Text>
      <div className='flex direction-column align-center'>
        <Button dark={true} onClick={handleUnstake} disabled={disabled} loading={unstakeLoading}>
          {unstakeLoading ? 'Processing...' : 'Confirm unstake'}
        </Button>
      </div>
    </>
  );
}
